import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import SummaryApi from "../../common/SummaryApi";
import { mergeGuestCart } from "../../helpers/cartHelper";
import { useCart } from "../../context/CartContext";
import { useDispatch } from "react-redux";
import { setUserDetails } from "../../store/userSlice";
import { Link } from "react-router-dom";

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { refreshCart } = useCart();
  const otpInputRefs = useRef([]);

  // View State: 'login' | 'signup-email' | 'signup-otp'
  const [view, setView] = useState("login");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(59); // Timer state

  // Form States
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupEmail, setSignupEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Visibility Toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Timer logic
  useEffect(() => {
    let interval = null;
    if (view === "signup-otp" && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [view, timer]);

  useEffect(() => {
    if (view === "signup-otp") {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    }
  }, [view]);

  if (!isOpen) return null;

  // --- HANDLERS ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(SummaryApi.signIn.url, loginData);
      localStorage.setItem("token", data.token);
      const userRes = await axios.get(SummaryApi.getProfile.url, {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      dispatch(setUserDetails(userRes?.data?.data));
      await mergeGuestCart(data.token);
      refreshCart();
      toast.success("Login successful!");
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(SummaryApi.register.url, { email: signupEmail });
      if (response.data.success) {
        toast.success(response.data.message);
        setView("signup-otp");
        setTimer(59); // Reset timer when moving to OTP view
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (timer > 0) return;
    setOtp(["", "", "", ""]);
    handleSendOtp();
    setTimer(59); // Reset timer on resend
    setTimeout(() => {
      otpInputRefs.current[0]?.focus();
    }, 100);
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const payload = { email: signupEmail, otp: otp.join(""), password };
      const response = await axios.post(SummaryApi.verifyOtp.url, payload);
      if (response.data.success) {
        toast.success("Account created! Please login.");
        setLoginData({ ...loginData, email: signupEmail });
        setView("login");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // --- OTP LOGIC ---
  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 3) otpInputRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) otpInputRefs.current[index - 1].focus();
    if (e.key === "ArrowLeft" && index > 0) otpInputRefs.current[index - 1].focus();
    if (e.key === "ArrowRight" && index < 3) otpInputRefs.current[index + 1].focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const data = e.clipboardData.getData("text").replace(/[^0-9]/g, "").split("");
    const newOtp = [...otp];
    data.forEach((char, idx) => {
      if (idx < 4) newOtp[idx] = char;
    });
    setOtp(newOtp);
    if (otpInputRefs.current[Math.min(data.length, 3)]) otpInputRefs.current[Math.min(data.length, 3)].focus();
  };

  const signupInputClass = "block px-4 py-3 w-full text-sm text-gray-900 bg-blue-50 rounded-md border-2 border-blue-200 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer placeholder-transparent";
  const signupLabelClass = "absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] left-3 bg-white px-2 text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-[-50%] peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:bg-white peer-focus:px-2 peer-focus:text-blue-600";

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl relative overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-black z-10 p-1">
          <FaTimes size={20} />
        </button>

        <div className="p-8">
          {view === "login" && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">Login into your account</h2>
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-1">
                  <label className="text-sm text-gray-600">Email Address</label>
                  <div className="relative">
                    <input type="email" required placeholder="alex@email.com" className="w-full px-4 py-2 pr-12 border bg-gray-100 rounded-md focus:outline-none" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} />
                    <div className="absolute top-0 right-0 h-full w-10 flex items-center justify-center bg-[#007BFF] rounded-md text-white">
                      <MdOutlineEmail />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-gray-600">Password</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} required placeholder="Enter password" className="w-full px-4 py-2 pr-12 border bg-gray-100 rounded-md focus:outline-none" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute top-0 right-0 h-full w-10 flex items-center justify-center bg-[#007BFF] rounded-md text-white">
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </button>
                  </div>
                </div>
                <button disabled={loading} className="w-full bg-[#007BFF] text-white py-2.5 rounded-md font-semibold hover:bg-blue-600 transition">
                  {loading ? "Logging in..." : "Login Now"}
                </button>

                <div className="flex items-center my-6">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-2 text-gray-400 text-sm">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
                <div className="text-sm text-gray-500 text-center">
                  Don't have an account? <button type="button" onClick={() => setView("signup-email")} className="text-blue-600 font-bold underline">Sign Up</button>
                </div>
              </form>
            </div>
          )}

          {view === "signup-email" && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300 text-center">
              <h2 className="text-2xl font-bold mb-8">Create Account</h2>
              <form onSubmit={handleSendOtp}>
                <div className="relative mb-6 text-left">
                  <input type="email" required className={signupInputClass} placeholder=" " value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} />
                  <label className={signupLabelClass}>Email Address</label>
                </div>
                <button disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium transition">
                  {loading ? "Sending..." : "Continue"}
                </button>
              </form>

              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-2 text-gray-400 text-sm">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              <div className="text-sm text-gray-500">
                Already have an account? <button type="button" onClick={() => setView("login")} className="text-blue-600 font-bold underline">Sign in</button>
              </div>
              <div className="mt-8 text-[10px] md:text-[12px] text-gray-500">
            By proceeding, you acknowledge and accept Regeestex{' '}
            <Link
              to="/terms-and-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-black transition-colors font-medium"
            >
              Terms of Use
            </Link>
            {' '}and{' '}
            <Link 
              to="/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-black transition-colors font-medium"
            >
              Privacy Policy
            </Link>.
          </div>
            </div>
          )}

          {view === "signup-otp" && (
            <div className="animate-in zoom-in duration-300 text-center">
              <h2 className="text-xl font-bold mb-2">OTP Verification</h2>
              <p className="text-xs text-gray-600 mb-1">Sent to {signupEmail}</p>
              <button onClick={() => setView("signup-email")} className="text-xs text-blue-500 hover:underline mb-6">
                Change Email
              </button>

              <form onSubmit={handleVerifyAndRegister}>
                <div className="flex justify-center gap-4 mb-8">
                  {otp.map((digit, i) => (
                    <input key={i} ref={(el) => (otpInputRefs.current[i] = el)} type="text" maxLength={1} value={digit} onPaste={handleOtpPaste} onKeyDown={(e) => handleOtpKeyDown(e, i)} onChange={(e) => handleOtpChange(e.target.value, i)} className="w-10 h-10 text-center text-xl font-bold border-b-2 border-gray-400 focus:border-blue-600 focus:outline-none bg-transparent" />
                  ))}
                </div>
                <div className="space-y-6 mb-6 text-left">
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} required className={`${signupInputClass} pr-10`} placeholder=" " onChange={(e) => setPassword(e.target.value)} />
                    <label className={signupLabelClass}>New Password</label>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-4 text-gray-400">
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showConfirmPassword ? "text" : "password"} required className={`${signupInputClass} pr-10`} placeholder=" " onChange={(e) => setConfirmPassword(e.target.value)} />
                    <label className={signupLabelClass}>Confirm Password</label>
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-4 text-gray-400">
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-6">
                  {timer > 0 ? (
                    <span className="text-gray-400">Resend OTP in <span className="font-bold text-blue-500">{timer}s</span></span>
                  ) : (
                    <>
                      Didn't receive the OTP?{" "}
                      <button type="button" onClick={handleResendOtp} className="text-blue-500 font-medium hover:underline">
                        Resend OTP
                      </button>
                    </>
                  )}
                </div>

                <button disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium transition">
                  {loading ? "Verifying..." : "Confirm & Register"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;